import {
    CONTENTITEM_TYPE_BLOGENTRY,
    CONTENTITEM_TYPE_EPISODE,
    CONTENTITEM_TYPE_FEATURE,
    CONTENTITEM_TYPE_SEGMENT,
    CONTENTITEM_TYPE_SPONSORED_CONTENT,
    CONTENTITEM_TYPE_STORY,
} from "../../pages/Detail/constants";
import logger from "../logger/library";
import {
    TYPE_POLOPOLY_IMAGE,
    TYPE_POLOPOLY_MEDIA,
    TYPE_POLOPOLY_PHOTOGALLERY,
} from "../storyBodyMarkupBuilder/constants";
import {
    CONTENTITEM_MAPPING_TO_SCHEMA_TYPE,
    SCHEMA_PUBLISHER,
    SCHEMA_TYPES,
} from "./constants";
import {
    encodeForJsonLD,
    getAuthorObject,
    getImageObject,
    getMediaObject,
    timestampToISOString,
} from "./library";

/**
 * Get full JSON-LD schema (structured data) for a given contentItem.
 *
 * Types of `contentItem`s handled by this function:
 * - story
 * - episode
 * - segment
 * - feature
 * - sponsoredcontent
 * - blogentry
 *
 * TODO: implement Photogallery contentItem
 *
 * TODO: implement Recipe contentItem
 *
 * @param {Object} contentItem - contentItem object obtained from the ContentItem query
 * @returns {Object | null} the constructed JSON-LD schema or null for contentItems that are not implemented
 */
export function getStoryPageJsonLdSchema(contentItem) {
    switch (contentItem?.type) {
        // https://schema.org/ReportageNewsArticle
        case CONTENTITEM_TYPE_STORY:
        // https://schema.org/BlogPosting
        // eslint-disable-next-line no-fallthrough
        case CONTENTITEM_TYPE_BLOGENTRY:
        // https://schema.org/NewsArticle
        // eslint-disable-next-line no-fallthrough
        case CONTENTITEM_TYPE_FEATURE:
            return getJsonLdSchemaByType(contentItem, {
                wordCount: contentItem?.wordcount || undefined,
            });
        // https://schema.org/AdvertiserContentArticle
        case CONTENTITEM_TYPE_SPONSORED_CONTENT:
            return getJsonLdSchemaByType(contentItem, {
                wordCount: contentItem?.wordcount || undefined,
                ...getJsonLdSchemaForSponsor(contentItem?.sponsor),
            });
        // https://schema.org/Episode
        case CONTENTITEM_TYPE_EPISODE:
            return getJsonLdSchemaByType(
                contentItem,
                getAssociatedMedia(contentItem?.episode?.media)
            );
        // https://schema.org/Clip
        case CONTENTITEM_TYPE_SEGMENT:
            return getJsonLdSchemaByType(
                contentItem,
                getAssociatedMedia(contentItem?.segment?.media)
            );

        default:
            return null;
    }
}

/**
 * Returns a JSON-LD schema for a given contentItem
 *
 * @param {Object} contentItem - contentItem object obtained from the ContentItem query
 * @param {Object} [additionalSchema] - additional properties/items to be supplied to the JSON-LD schema
 * @returns {Object} the constructed JSON-LD schema
 */
function getJsonLdSchemaByType(contentItem, additionalSchema = {}) {
    return {
        ...CONTENTITEM_MAPPING_TO_SCHEMA_TYPE[contentItem.type],
        ...getBaseSchemaForJsonLD(contentItem),
        ...getJsonLdSchemaFromLeadMedia(contentItem?.leadMedia),
        ...extractJsonLdSchemaFromBodyParsed(contentItem?.body?.parsed),
        ...getArticleSection(contentItem?.section?.path),
        ...additionalSchema,
        "@id": contentItem?.url,
    };
}

/**
 * Generates a base JSON-LD schema sharable across various contentItem types.
 *
 * @param {Object} params - contentItem object obtained from the ContentItem query
 * @param {String} params.title
 * @param {String} params.description
 * @param {String} params.deck
 * @param {String} params.publishedAt
 * @param {String} params.updatedAt
 * @param {Object} params.image
 * @param {Object[]} params.authors
 * @returns {Object} the JSON-LD schema object
 */
function getBaseSchemaForJsonLD({
    title,
    description,
    deck,
    publishedAt,
    updatedAt,
    image,
    authors,
}) {
    return {
        "@context": "https://schema.org",
        name: encodeForJsonLD(title),
        alternateName: encodeForJsonLD(deck || description || ""),
        headline: encodeForJsonLD(title || ""),
        description: encodeForJsonLD(description || ""),
        datePublished: timestampToISOString(publishedAt),
        dateModified: timestampToISOString(updatedAt),
        thumbnailUrl: image?.aspects?.ratio16x9?.url || "",
        publisher: { ...SCHEMA_PUBLISHER },
        author: getAuthorObject(authors),
    };
}

/**
 * Returns media JSON-LD schema (audio or video) useful for Episode and Segment contentItems
 *
 * @param {Object} [mediaObject] - contentItem.episode.media or contentItem.segment.media obtained from the ContentItem query
 * @returns {Object} - the associatedMedia JSON-LD schema
 */
export function getAssociatedMedia(mediaObject) {
    if (!mediaObject) {
        return {};
    }

    return {
        associatedMedia: [getMediaObject(mediaObject)],
    };
}

/**
 * Returns relevant JSON-LD schema for the body.parsed content of a contentItem.
 * Handles images, audios, and videos.
 *
 * TODO: PhotoGallery schema implementation to be added soon
 *
 * @param {Object} leadMedia - leadMedia object obtained from the ContentItem query (e.g. contentItem.leadMedia)
 * @returns {Object} image / audio / video JSON-LD schema
 */
export function getJsonLdSchemaFromLeadMedia(leadMedia) {
    // leadMedia is null or undefined
    if (!leadMedia) {
        return {};
    }

    if (leadMedia?.__typename === "Image") {
        // get image schema
        return { image: [getImageObject(leadMedia)] };
    }

    if (leadMedia?.type === "video" || leadMedia?.type === "audio") {
        return {
            [leadMedia.type]: [getMediaObject(leadMedia)], // leadMedia.type will be either "video" or "audio" so this fits nicely with the schema.org implementation
        };
    }

    // TODO: to implement image object schema for photogallery
    if (leadMedia?.type === "photogallery") {
        // supposed to be in the shape of { image: { "@type": "ImageObject", ... }[] }
        return {};
    }

    // should never be the case, but if by chance we reach this part we throw an error and capture in Sentry to investigate further
    const errorMessage = `unhandled LeadMedia ${leadMedia?.type} type`;
    logger.error(
        `(getLeadMediaSchemaForJsonLd) ${errorMessage}`,
        new Error(errorMessage)
    );

    return {};
}

/**
 * Returns relevant JSON-LD schema for the body.parsed content of a contentItem.
 * Handles images, audios, and videos.
 *
 * TODO: PhotoGallery schema implementation to be added soon
 *
 * @param {Object} bodyParsed - body.parsed obtained from the contentItem query
 * @returns {Object} image / audio / video JSON-LD schema
 */
export function extractJsonLdSchemaFromBodyParsed(bodyParsed) {
    const content = bodyParsed?.[0]?.content?.[0];

    if (!content) {
        return {};
    }

    const { type, content: actualContentData } = content;

    // "polopoly_image" means it's an image
    if (type === TYPE_POLOPOLY_IMAGE) {
        return { image: [getImageObject(actualContentData, "ratioCustom")] };
    }

    // "polopoly_media" means it's either audio or video
    if (type === TYPE_POLOPOLY_MEDIA) {
        const mediaType = actualContentData?.type || "video";
        return { [mediaType]: [getMediaObject(actualContentData)] };
    }

    // "polopoly_photogallery" means it's a photogallery
    if (type === TYPE_POLOPOLY_PHOTOGALLERY) {
        // TODO: to implement image object schema for photogallery
        // supposed to be in the shape of { image: { "@type": "ImageObject", ... }[] }
        return {};
    }

    return {};
}

/**
 * For a given hierarchy of sections (e.g. /News/Canada/) return the most specific section
 *
 * @param {String} sectionPath - section.path coming from GraphQL for a particular contentItem (see contentItem.js query for more details)
 * @returns {Object}
 *
 * @example
 * getArticleSection("News/Canada") // { articleSection: "Canada" }
 * getArticleSection("News") // { articleSection: "News" }
 * getArticleSection(undefined | null | "") // {}
 */
export function getArticleSection(sectionPath) {
    if (!sectionPath || typeof sectionPath !== "string") return {};

    const splitSections = sectionPath.split("/");
    const articleSection = splitSections[splitSections.length - 1];

    return {
        articleSection,
    };
}

/**
 * Returns https://schema.org/sponsor relevant with https://schema.org/Organization
 *
 * @param {Object} sponsor - the sponsor object obtained from ContentItem query
 * @returns {Object} - the sponsor JSON-LD schema for SponsoredContent pages
 */
export function getJsonLdSchemaForSponsor(sponsor) {
    if (!sponsor?.name || !sponsor?.link) {
        return {};
    }

    return {
        sponsor: {
            ...SCHEMA_TYPES.Organization,
            name: sponsor.name,
            logo: getImageObject(sponsor?.image, "ratioCustom"),
            url: sponsor?.link,
        },
    };
}
